/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader
} from 'reactstrap';
import { companyTrackerUserRoles, UserRoles } from '@constants/user';
import './styles.scss';
import { getChecked, getFullName, handleRoleRadioChange } from '@utils/user';
import G17Client from '@services/G17Client';
import { BasicAlert } from '@g17eco/molecules/alert';
import { ManagementInvitation } from '@g17eco/types/onboarding';

interface UserInvitationProps {
  handleReload: () => void;
  invitation: ManagementInvitation;
  toggle: () => void;
}

export const RequestToJoinModal = ({ invitation, toggle, handleReload }: UserInvitationProps) => {

  const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([UserRoles.Viewer]);
  const [errroMessage, setErrorMessage] = useState('');

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const role = e.target.value as UserRoles;
    const checked = e.target.checked;
    setSelectedRoles(prevRoles => handleRoleRadioChange(role, checked ? 'add' : 'remove', prevRoles));
  }

  const triggerAction = (action: 'accept' | 'reject') => {
    G17Client.joinRequestAction({
      onboardingId: invitation._id,
      initiativeId: invitation.initiativeId,
      roles: selectedRoles,
      action,
    }).then(() => {
        handleReload();
        toggle();
      }).catch(e => {
        setErrorMessage(e.message);
    })
  }

  const renderRolesSelectors = () => {
    const adminRole = companyTrackerUserRoles.find(item => item.code === UserRoles.Manager);
    return (
      <div className='mt-4'>
        {adminRole && (
          <FormGroup check className='py-0'>
            <Input
              type='checkbox'
              id={adminRole.code}
              name={adminRole.code}
              value={adminRole.code}
              onChange={handleRoleChange}
              checked={selectedRoles.includes(adminRole.code)}
            />
            <Label check for={adminRole.code}>
              <span className='strong'>{adminRole.label}</span>
            </Label>
          </FormGroup>
        )}
        <hr className='my-3 opacity-100 border-ThemeBorderDefault' />
        <div className='mt-4'>
          <h5>User permissions</h5>
          {companyTrackerUserRoles
            .filter((role) => !role.disabled && role.code !== UserRoles.Manager)
            .map((role) => {
              const id = `role_${role.code}`;
              return (
                <FormGroup check key={id} onClick={e => e.stopPropagation()} className='py-1'>
                  <Input
                    type='radio'
                    id={id}
                    name='user_roles'
                    value={role.code}
                    onChange={handleRoleChange}
                    checked={getChecked(role.code, selectedRoles)}
                  />
                  <Label check for={id}>
                    <span className='strong'>
                      {role.code === UserRoles.Contributor || role.code === UserRoles.Verifier ? `${role.label} only` : role.label}
                    </span>
                  </Label>
                </FormGroup>
              );
            })}
        </div>
      </div>
    );
  }

  return (
    <Modal className='request-to-join-modal' isOpen={true} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>Access request</ModalHeader>
      <ModalBody className='mx-3'>
        <BasicAlert type={'danger'}>{errroMessage}</BasicAlert>
        <div>Request to join from</div>
        <div className='strong'>{getFullName(invitation.user, '-')}</div>
        <div className='dont_translate'>{invitation.user.email}</div>

        <div className='mt-3'>Message</div>
        <div className='mt-2 message-container'>{invitation.user.message ?? 'Not provided'}</div>

        {renderRolesSelectors()}

        <div className='mt-5 d-flex justify-content-between'>
          <div>
            <Button outline color='danger' onClick={() => triggerAction('reject')}>Reject request</Button>
          </div>
          <div>
            <Button color='primary' onClick={() => triggerAction('accept')}>Accept</Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}
